import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CalendarClock, Plus, X } from "lucide-react";

export default function InterviewsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [apps, setApps] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ application_id: "", scheduled_at: "", duration_minutes: 45, type: "virtual", location: "", meeting_link: "", notes: "" });

  const load = async () => {
    const { data } = await api.get("/interviews");
    setItems(data.items || []);
  };
  useEffect(() => {
    load();
    if (user?.role !== "candidate") {
      api.get("/applications").then(r => setApps(r.data.items || []));
    }
  }, [user]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/interviews", { ...form, scheduled_at: new Date(form.scheduled_at).toISOString() });
      toast.success("Interview scheduled");
      setCreating(false);
      setForm({ application_id: "", scheduled_at: "", duration_minutes: 45, type: "virtual", location: "", meeting_link: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not schedule"); }
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow mb-2">Scheduling</div>
          <h1 className="font-display text-3xl font-bold text-navy">Interviews</h1>
        </div>
        {user?.role !== "candidate" && (
          <button onClick={() => setCreating(true)} className="btn-navy"><Plus className="w-4 h-4" /> Schedule interview</button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {items.map(i => (
          <div key={i.id} className="border border-slate-200 rounded-xl p-5 bg-white">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500">{i.type} • {i.status}</div>
                <div className="font-display text-lg text-navy font-semibold mt-1">{i.application?.job_title}</div>
                <div className="text-sm text-slate-600">{i.application?.candidate_name} → {i.application?.employer_name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-500">{new Date(i.scheduled_at).toLocaleString()}</div>
                <div className="text-xs text-slate-500">{i.duration_minutes} min</div>
              </div>
            </div>
            {i.meeting_link && <a href={i.meeting_link} target="_blank" rel="noreferrer" className="mt-3 inline-block text-sm text-navy hover:text-gold">Join link →</a>}
            {i.location && <div className="mt-2 text-sm text-slate-600">📍 {i.location}</div>}
            {i.notes && <div className="mt-2 text-sm text-slate-600 border-l-2 border-slate-200 pl-3">{i.notes}</div>}
          </div>
        ))}
        {items.length === 0 && <div className="text-slate-500 col-span-full"><CalendarClock className="w-8 h-8 text-slate-300 mb-2" />No interviews scheduled yet.</div>}
      </div>

      {creating && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setCreating(false)}>
          <form onSubmit={create} className="bg-white rounded-xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <div className="font-display text-xl font-semibold text-navy">Schedule interview</div>
              <button type="button" onClick={() => setCreating(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div>
                <label className="text-xs text-slate-500">Application</label>
                <select required value={form.application_id} onChange={e => setForm({...form, application_id: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                  <option value="">Select…</option>
                  {apps.map(a => <option key={a.id} value={a.id}>{a.candidate_name} — {a.job_title}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Date & time</label>
                  <input type="datetime-local" required value={form.scheduled_at} onChange={e => setForm({...form, scheduled_at: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Duration (min)</label>
                  <input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value)})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Type</label>
                  <select value={form.type} onChange={e => setForm({...form, type: e.target.value})} className="w-full border rounded px-3 py-2 mt-1">
                    <option value="virtual">Virtual</option><option value="phone">Phone</option><option value="in-person">In-person</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Meeting link</label>
                  <input value={form.meeting_link} onChange={e => setForm({...form, meeting_link: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-slate-500">Location (optional)</label>
                  <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-slate-500">Notes</label>
                  <textarea rows={2} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full border rounded px-3 py-2 mt-1" />
                </div>
              </div>
              <button type="submit" className="btn-navy w-full justify-center">Schedule</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
